import { useCallback, useContext, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import moment from "moment";
import CollapsibleTable from "../../components/collapsible-table";
import Avatar from "@mui/material/Avatar";
import AddIcon from "@mui/icons-material/Add";
import Button from "@mui/material/Button";
import useCompanies from "../../hooks/companies";
import TagColor from "../../components/tag-color";
import UploadButton from "../../components/upload-button";
import Drawer from "@mui/material/Drawer";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import OutlinedInput from "@mui/material/OutlinedInput";
import AppBar from "@mui/material/AppBar";
import Typography from "@mui/material/Typography";
import Toolbar from "@mui/material/Toolbar";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import usePrices from "../../hooks/prices";
import { UserContext } from "../../context";
import Chip from "@mui/material/Chip";

const PricesConfig = () => {
  const userWorker = useContext(UserContext);
  const { get: getCompanies } = useCompanies();
  const { get: getPrices, create: createQuestion, deleteData } = usePrices();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [pricesHistory, setPricesHistory] = useState<Prices[]>([]);
  const [company, setCompany] = useState<Company>();
  const [isLoading, setIsLoading] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    control,
    formState: { errors },
  } = useForm<Prices>({});

  const loadData = useCallback(() => {
    const fn = async () => {
      const resp = await getCompanies();
      setCompanies(resp);
    };
    fn();
  }, []);

  const loadPrices = useCallback(async () => {
    if (company) {
      const resp = await getPrices(company.id);
      setPricesHistory(resp);
    }
  }, [company]);

  useEffect(() => {
    loadPrices();
  }, [loadPrices]);
  const onSubmit: SubmitHandler<Prices> = (data) => {
    createQuestion({
      ...data,
      companyId: company?.id,
      updatedAt: new Date(),
      updatedBy: {
        id: userWorker.user?.id,
        name: userWorker.user?.name,
      },
    });
    loadPrices();
    toggleDrawer();
    reset();
  };

  useEffect(() => {
    loadData();
  }, [loadData]);

  const toggleDrawer = () => {
    setOpenDrawer(!openDrawer);
    if (pricesHistory.length > 0) {
      const { perHour, perMile } = pricesHistory[0];
      setValue("perHour", perHour);
      setValue("perMile", perMile);
    } else {
      setValue("perHour", 0);
      setValue("perMile", 0);
    }
  };
  const handleChange = (event: SelectChangeEvent<string>) => {
    const {
      target: { value },
    } = event;
    setCompany(companies.filter((item) => item.id === value)[0] || undefined);
  };
  const handleDelete = async (questionId: string) => {
    await deleteData(questionId);
    loadPrices();
  };
  return (
    <div className="pt-[64px] w-full">
      <AppBar
        position="static"
        variant="outlined"
        color="secondary"
        className="flex flex-row"
      >
        <Toolbar>
          <Typography
            component="h1"
            variant="h6"
            color="inherit"
            noWrap
            sx={{ flexGrow: 1 }}
          >
            Prices config
          </Typography>
          <FormControl sx={{ m: 1, width: 300 }}>
            <InputLabel id="demo-multiple-name-label">Company</InputLabel>
            <Select
              labelId="demo-multiple-name-label"
              id="demo-multiple-name"
              value={company?.id}
              onChange={handleChange}
              input={<OutlinedInput label="Company" />}
            >
              {companies.map((company) => (
                <MenuItem key={company.id} value={company.id}>
                  {company.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Toolbar>
      </AppBar>
      {company ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            padding: "24px",
            width: "100%",
          }}
        >
          <div className="pb-[24px]">
            <Button
              size="small"
              variant="contained"
              color="secondary"
              onClick={toggleDrawer}
            >
              <AddIcon />
              set price
            </Button>
          </div>

          <CollapsibleTable
            header={[
              "Price per mile",
              "price per hour",
              "updated at",
              "updated by",
              "",
              "",
            ]}
            rows={pricesHistory.map((prices, index) => {
              const { seconds, nanoseconds } = prices.updatedAt as unknown as {
                seconds: number;
                nanoseconds: number;
              };

              const auxi = moment
                .unix(seconds)
                .add(nanoseconds / 1000000, "milliseconds");
              const dateFormated = auxi.format("HH:mm  DD/MM/YYYY");

              return {
                id: prices.id,
                cells: [
                  prices.perMile + "£/mi",
                  prices.perHour + "£/h",
                  dateFormated,
                  prices.updatedBy.name,

                  index === 0 ? (
                    <Chip label="Current prices" color="success" />
                  ) : (
                    ""
                  ),
                ],
              };
            })}
          />
          <Drawer anchor={"right"} open={openDrawer} onClose={toggleDrawer}>
            <div className="p-[24px] pt-[88px] w-[400px]">
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="flex flex-col gap-[24px]"
              >
                <TextField
                  id="outlined-basic"
                  label="Price per mile"
                  variant="outlined"
                  {...register("perMile", { required: true })}
                />
                <TextField
                  id="outlined-basic"
                  label="Price per hour"
                  variant="outlined"
                  {...register("perHour", { required: true })}
                />

                <Button type="submit" variant="contained" color="secondary">
                  Save
                </Button>
              </form>
            </div>
          </Drawer>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            padding: "24px",
            width: "100%",
          }}
        >
          <Typography
            component="h1"
            variant="h6"
            color="inherit"
            noWrap
            sx={{ flexGrow: 1 }}
          >
            Please select a company
          </Typography>
        </Box>
      )}
    </div>
  );
};

export default PricesConfig;
